import { xingshanRawServiceKeyboard } from "../../sdkApi/keyboard/commonApi";
import { initXingshanSdkForConfiguration } from "../connectDevice/sdk/xingshan";

function readEquipmentData(): Record<string, unknown> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      window.sessionStorage.getItem("eweadn:session:equipmentData") ??
      window.localStorage.getItem("equipmentData");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return parsed?.solutionRovider ? parsed : null;
  } catch {
    return null;
  }
}

export const DRIVER_TAB_HARDWARE_RESYNC_EVENT =
  "eweadn:driverTabHardwareResynced";

/** 桌面端退出后 OS 释放 HID 需要更长时间，带退避重试 */
const OWNER_HANDOFF_DELAYS_MS = [150, 500, 1000, 2000];

let handoffInFlight: Promise<boolean> | null = null;

function dispatchHardwareResync(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(DRIVER_TAB_HARDWARE_RESYNC_EVENT));
}

async function releaseXingshanHidForStandby(): Promise<void> {
  try {
    const raw = xingshanRawServiceKeyboard as {
      closeDevice?: () => Promise<unknown>;
    };
    if (typeof raw.closeDevice === "function") {
      await raw.closeDevice();
    }
  } catch {
    /* ignore */
  }
}

async function resyncXingshanHidForOwner(): Promise<boolean> {
  const eq = readEquipmentData();
  if (String(eq?.solutionRovider ?? "") !== "xingshan") {
    dispatchHardwareResync();
    return true;
  }

  for (let i = 0; i < OWNER_HANDOFF_DELAYS_MS.length; i++) {
    const delay = OWNER_HANDOFF_DELAYS_MS[i];
    await new Promise((r) => setTimeout(r, delay));
    try {
      const ok = await initXingshanSdkForConfiguration(eq, {
        skipPresenceRestart: true,
      });
      if (ok) {
        dispatchHardwareResync();
        return true;
      }
    } catch (e) {
      console.warn(
        `[device-session] Owner HID resync attempt ${i + 1} failed:`,
        e
      );
    }
  }

  console.warn(
    "[device-session] Owner HID resync exhausted retries; will retry on next heartbeat"
  );
  return false;
}

/**
 * Owner ↔ Standby HID 交接（星闪）
 * @returns handoff 是否成功（standby 释放恒为 true；owner 重连失败为 false）
 */
export async function applyDeviceSessionRoleTransition(
  nextRole: "owner" | "standby",
  prevRole: "owner" | "standby" | "offline" | null
): Promise<boolean> {
  if (nextRole === prevRole) return true;
  if (handoffInFlight) {
    const prior = await handoffInFlight;
    if (nextRole === prevRole) return prior;
  }

  handoffInFlight = (async (): Promise<boolean> => {
    if (nextRole === "standby") {
      await releaseXingshanHidForStandby();
      return true;
    }
    if (nextRole === "owner") {
      return resyncXingshanHidForOwner();
    }
    return true;
  })();

  try {
    return await handoffInFlight;
  } finally {
    handoffInFlight = null;
  }
}
